import { Affix, Combobox } from '@warp-ds/react';
import { type FC, type SyntheticEvent, useEffect, useState } from 'react';
import { useDebounce } from '../../hooks/useDebounce';
import { addressSearch } from '../../pristips/addressSearch';
import { highlightAddressMatch } from '../../pristips/highlightAddressMatch';
import type { Address } from '../../pristips/inputForm';

interface Props {
    address: Address | undefined;
    setAddress: (value?: Address) => void;
    error: string | undefined;
    clearError: () => void;
}

type ComboboxOption = {
    value: string;
    address: Address;
};

export const AddressSearch: FC<Props> = ({ address, setAddress, error, clearError }) => {
    const [value, setValue] = useState(address?.label ?? '');
    const [options, setOptions] = useState<ComboboxOption[]>([]);

    const debouncedQuery = useDebounce(value, 100);

    useEffect(() => {
        if (debouncedQuery && debouncedQuery.length >= 3) {
            addressSearch(debouncedQuery)
                .then((res) => setOptions(res.map((option) => ({ value: option.label, address: option }))))
                .catch(() => setOptions([]));
        }
    }, [debouncedQuery]);

    const removeSpaceBetweenNumberAndText = /(\d)\s+(?=\D)/g;
    const handleChange = (newValue: string) => {
        setAddress(undefined);
        clearError();
        setValue(newValue.replace(removeSpaceBetweenNumberAndText, '$1'));
    };

    const handleSelect = (newValue: string | SyntheticEvent<HTMLInputElement>) => {
        const selectedOption = options.find((option) => option.value === newValue);
        if (selectedOption) {
            setAddress(selectedOption.address);
            setValue(selectedOption.address.label);
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Enter' && options.length === 1) {
            const only = options[0]!.address;
            setAddress(only);
            setValue(only.label);
            event.preventDefault();
        }
    };

    return (
        <div>
            <Combobox
                listClassName="s-bg max-h-[132px] md:max-h-[122px] overflow-y-auto"
                label="Adresse*"
                autoCapitalize="off"
                autoComplete="off"
                data-1p-ignore
                autoCorrect="off"
                enterKeyHint="search"
                highlightValueMatch={highlightAddressMatch}
                disableStaticFiltering
                onChange={handleChange}
                onSelect={handleSelect}
                options={options}
                spellCheck="false"
                value={value}
                placeholder="Søk etter adresse"
                invalid={!!error}
                onKeyDown={handleKeyDown}
            >
                <Affix suffix search aria-label="Søk på adresse" />
            </Combobox>
            {error && <p className="mt-4 text-xs s-text-negative">{error}</p>}
        </div>
    );
};
